import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { formatDate } from '../../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  UserCircle, Phone, EnvelopeSimple, DotsThree, Pencil,
  UserCircleCheck, UserCircleMinus, Trash,
} from '@phosphor-icons/react';

export const EmployeesTable = ({
  loading, filteredEmployees, selectedIds, isAllSelected, isSomeSelected,
  toggleSelectAll, toggleSelect, getStatusBadge,
  onOpenModal, onActivate, onDeactivate, onTerminate, onDelete,
}) => {
  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (filteredEmployees.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="text-center py-12">
            <UserCircle size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No employees found</p>
            <Button variant="link" className="text-indigo-600 mt-2" onClick={() => onOpenModal()}>
              Add your first employee
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-employees"
                  className={isSomeSelected ? 'opacity-60' : ''}
                />
              </TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Employee ID</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Hire Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>EVV</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployees.map((employee) => (
              <TableRow
                key={employee.id}
                className={selectedIds.includes(employee.id) ? 'bg-indigo-50' : ''}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(employee.id)}
                    onCheckedChange={() => toggleSelect(employee.id)}
                    data-testid={`select-employee-${employee.id}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-50 rounded-full flex items-center justify-center">
                      <span className="text-indigo-700 font-semibold text-sm">
                        {employee.first_name[0]}{employee.last_name[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {employee.first_name} {employee.last_name}
                      </p>
                      {employee.sandata_employee_id && (
                        <p className="text-xs text-slate-500">Sandata: {employee.sandata_employee_id}</p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell><span className="font-mono text-sm">{employee.employee_id}</span></TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{employee.job_title}</p>
                    {employee.certification_type && (
                      <p className="text-xs text-slate-500">{employee.certification_type}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {employee.phone && (
                      <p className="text-sm flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />{employee.phone}
                      </p>
                    )}
                    {employee.email && (
                      <p className="text-sm flex items-center gap-1">
                        <EnvelopeSimple size={12} className="text-slate-400" />{employee.email}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>{employee.hire_date ? formatDate(employee.hire_date) : '—'}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={getStatusBadge(employee.status)}
                    title={
                      employee.status === 'Needs completion'
                        ? 'Auto-created from a timesheet scan — fill in Employee ID and hire date before linking to EVV visits.'
                        : undefined
                    }
                  >
                    {employee.status === 'Needs completion'
                      ? '⚠ Needs completion'
                      : employee.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {(employee.ssn_full || employee.npi || employee.employee_medicaid_id) ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                      data-testid={`evv-ready-${employee.id}`}
                    >
                      Ready
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
                      data-testid={`evv-missing-${employee.id}`}
                    >
                      Missing ID
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`employee-menu-${employee.id}`}>
                        <DotsThree size={18} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onOpenModal(employee)}>
                        <Pencil size={14} className="mr-2" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {employee.status !== 'Active' && (
                        <DropdownMenuItem onClick={() => onActivate(employee.id)}>
                          <UserCircleCheck size={14} className="mr-2 text-emerald-600" />Activate
                        </DropdownMenuItem>
                      )}
                      {employee.status === 'Active' && (
                        <DropdownMenuItem onClick={() => onDeactivate(employee.id)}>
                          <UserCircleMinus size={14} className="mr-2 text-amber-600" />Deactivate
                        </DropdownMenuItem>
                      )}
                      {employee.status !== 'Terminated' && (
                        <DropdownMenuItem onClick={() => onTerminate(employee.id)}>
                          <UserCircleMinus size={14} className="mr-2 text-red-600" />Terminate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onDelete(employee.id)} className="text-red-600">
                        <Trash size={14} className="mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
